/**
 * Pure geographic bbox helpers and default fetch for stop preload.
 */

import axios from "axios";
import type { FeatureCollection } from "geojson";
import type { LngLatBounds } from "maplibre-gl";

import { MAP_STOPS_PRELOAD } from "../../../config/global.configuration";
import type { StopsBboxGroupedResponseDto } from "../../../types/dtos/api/stop.dto";
import {
    bboxContains,
    bboxToPolygonFeatureCollection,
    computePrefetchBboxFromContext,
    computePrefetchScaleFromContext,
    expandBboxFromCenter,
    mapBoundsToBbox,
    shrinkBboxFromCenter,
    unionBboxes,
} from "../bboxPreload/bboxPreload.tools";
import type { MapBbox } from "../bboxPreload/bboxPreload.types";
import type { StopBbox, StopViewportContext } from "./stopPreload.types";

export {
    bboxContains,
    expandBboxFromCenter,
    shrinkBboxFromCenter,
    unionBboxes,
};

/**
 * Converts MapLibre bounds to {@link StopBbox}.
 *
 * @param bounds MapLibre lng/lat bounds from `map.getBounds()`.
 * @returns Stop preload bbox.
 */
export const mapBoundsToStopBbox = ( bounds: LngLatBounds ): StopBbox => mapBoundsToBbox( bounds );

/**
 * Builds a closed Polygon GeoJSON feature collection from a stop preload bbox.
 *
 * @param bbox Geographic bounds.
 * @returns FeatureCollection with one polygon feature.
 */
export const stopBboxToPolygonFeatureCollection = ( bbox: StopBbox ): FeatureCollection => bboxToPolygonFeatureCollection( bbox );

/**
 * Builds the API query string for GET /api/stops from a preload bbox.
 *
 * @param bbox Preload bounds (lat/lng order expected by backend).
 * @returns URL path including query (`/api/stops?min=...&max=...`).
 */
export const buildStopsBboxApiUrl = ( bbox: MapBbox ): string => `/api/stops?min=${ bbox.minLat },${ bbox.minLon }&max=${ bbox.maxLat },${ bbox.maxLon }`;

/**
 * Fetches grouped stops for a geographic bbox (injectable for tests).
 *
 * @param bbox Preload bounds.
 * @param signal Optional abort signal when the store is reset during flight.
 * @returns Grouped parent/child stop DTOs.
 */
export type FetchStopsForBbox = (
    bbox: StopBbox,
    signal?: AbortSignal,
) => Promise<StopsBboxGroupedResponseDto>;

/**
 * Default production fetch using the stops spatial index HTTP API.
 *
 * @param bbox Preload bounds.
 * @param signal Optional abort signal.
 * @returns Parsed grouped stop response.
 */
export const defaultFetchStopsForBbox: FetchStopsForBbox = async(
    bbox: StopBbox,
    signal?: AbortSignal,
): Promise<StopsBboxGroupedResponseDto> => {
    const response = await axios.get<StopsBboxGroupedResponseDto>(
        buildStopsBboxApiUrl( bbox ),
        { "signal": signal },
    );

    return response.data;
};

/**
 * Logs stop fetch errors, keeping the existing preload when the spatial index is not ready.
 *
 * @param error Thrown error.
 * @param signal Abort signal for the failed request.
 */
export const handleStopsFetchError = ( error: unknown, signal: AbortSignal ): void => {
    if ( signal.aborted ) {
        return;
    }

    if ( axios.isAxiosError( error ) && error.response?.status === 503 ) {
        console.warn( "Stops spatial index not ready (503); keeping existing preload until retry" );
    } else if ( axios.isAxiosError( error ) ) {
        console.error( "Stops fetch failed", error.response?.status );
    } else {
        console.error( "Stops fetch failed", error );
    }
};

/**
 * Linear scale applied to {@link StopViewportContext.visibleBbox} half-axes for prefetch.
 *
 * @param context Viewport snapshot from the map controller.
 * @returns Multiplier passed to {@link expandBboxFromCenter}.
 */
export const computeStopPrefetchScaleFromContext = ( context: StopViewportContext ): number => computePrefetchScaleFromContext( context, MAP_STOPS_PRELOAD );

/**
 * Computes the prefetch bbox from MapLibre visible bounds and zoom-dependent scale.
 *
 * @param context Viewport snapshot from the map controller.
 * @returns Bbox sent to GET /api/stops on the next fetch.
 */
export const computeStopPrefetchBboxFromContext = ( context: StopViewportContext ): StopBbox => computePrefetchBboxFromContext( context, MAP_STOPS_PRELOAD );
